 
import {useMemo} from 'react'
import {InlineRow} from '@/components/shared/InlineRow'
import {useQuery} from '@tanstack/react-query'
import {CheckCircle, XCircle, Clock, X} from 'lucide-react'
import {useSearchParams} from 'react-router-dom'
import {usePageTitle} from '@/hooks/use-page-title'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {Icon} from '@/components/shared/Icon'
import {routes} from '@/lib/routes'
import {Badge} from '@/components/ui/badge'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {CONCEPT_ICON} from '@/lib/concepts'
import {Pagination} from '@/components/ui/pagination'
import {useServerPagination, SortDir} from '@/hooks/use-server-pagination'
import {UnifiedSearchBar} from '@/components/shared/UnifiedSearchBar'
import {
    RestrictionRuleControllerAllDetectionsEndpoint,
} from '@/types/endpoint-contracts'
import {RestrictionRuleDetectionStatus, RestrictionRuleDetectionFilterParams} from '@/types/restriction-rule'
import type {RestrictionRuleDetectionLog} from '@/types/restriction-rule'
import {formatTimestamp} from '@/lib/date-utils'
import {BadgeVariant} from '@/components/ui/badge'
import {PageLayout} from '@/components/shared/PageLayout'
import {TextLink} from '@/components/shared/TextLink'
import {IconText} from '@/components/shared/IconText'
import {InlineEmptyText} from '@/components/shared/InlineEmptyText'

export function RestrictionRuleDetections() {
    const services = useServices()
    const i18n = useI18n()
    usePageTitle(i18n.restrictionRules.detectionHistoryPageTitle)
    const [searchParams, setSearchParams] = useSearchParams()
    const ruleIdParam = searchParams.get(RestrictionRuleDetectionFilterParams.ruleId)

    const {
        page, pageSize, queryString,
        setPage, setPageSize, pageSizeOptions,
        searchAndFilters, setSearchAndFilters,
    } = useServerPagination({
        endpoint: RestrictionRuleControllerAllDetectionsEndpoint,
        sortable: true,
        defaultSortBy: RestrictionRuleControllerAllDetectionsEndpoint.sort.DETECTION_STARTED_AT,
        defaultSortDir: SortDir.DESC,
    })

    const {data: rule} = useQuery({
        queryKey: ['restriction-rule', ruleIdParam],
        queryFn: () => services.restrictionRules.detail(ruleIdParam!),
        enabled: !!ruleIdParam,
    })

    const {data: detections, isLoading, error} = useQuery({
        queryKey: ['restriction-rule-detections', ruleIdParam, queryString, page, pageSize],
        queryFn: () => services.restrictionRules.allDetections(Object.fromEntries(new URLSearchParams(queryString))),
    })

    const getStatusIcon = (status: RestrictionRuleDetectionStatus) => {
        switch (status) {
            case RestrictionRuleDetectionStatus.SUCCESS:
                return <Icon icon={CheckCircle} className="text-status-success"/>
            case RestrictionRuleDetectionStatus.FAILED:
                return <Icon icon={XCircle} className="text-status-danger"/>
            default:
                return null
        }
    }

    const getStatusBadgeVariant = (status: RestrictionRuleDetectionStatus) => {
        switch (status) {
            case RestrictionRuleDetectionStatus.SUCCESS:
                return BadgeVariant.SUCCESS
            case RestrictionRuleDetectionStatus.FAILED:
                return BadgeVariant.DESTRUCTIVE
            default:
                return BadgeVariant.SECONDARY
        }
    }

    const clearRuleFilter = () => {
        searchParams.delete(RestrictionRuleDetectionFilterParams.ruleId)
        setSearchParams(searchParams)
    }

    const hasActiveFilters = useMemo(() => {
        return !!searchAndFilters.q
            || Object.keys(searchAndFilters.filters).length > 0
            || Object.keys(searchAndFilters.dynamicAttributes).length > 0
            || !!ruleIdParam
    }, [searchAndFilters, ruleIdParam])

    const filters = (
        <>
            <UnifiedSearchBar
                contract={RestrictionRuleControllerAllDetectionsEndpoint}
                value={searchAndFilters}
                onChange={setSearchAndFilters}
            />
            {ruleIdParam && rule && (
                <Badge variant={BadgeVariant.SECONDARY} className="items-center gap-1">
                    {i18n.restrictionRules.rule}: {rule.name}
                    <Icon
                        icon={X}
                        size="xs"
                        onClick={clearRuleFilter}
                        data-testid="btn-clear-rule-filter"
                        aria-label={i18n.common.clear}
                    />
                </Badge>
            )}
        </>
    )

    return (
        <PageLayout
            variant="list"
            icon={CONCEPT_ICON.restrictionRule}
            title={i18n.restrictionRules.ruleDetectionHistory as string}
            count={detections?.totalElements}
            description={hasActiveFilters ? i18n.common.matchingFilters : undefined}
            filters={filters}
            loading={isLoading}
            error={error}
            data-testid="restriction-rule-detections"
            emptyState={
                <InlineEmptyText>{i18n.restrictionRules.noDetections}</InlineEmptyText>
            }
            pagination={detections ? (
                <Pagination
                    page={page}
                    pageSize={pageSize}
                    totalItems={detections.totalElements}
                    totalPages={detections.totalPages}
                    onPageChange={setPage}
                    onPageSizeChange={setPageSize}
                    pageSizeOptions={pageSizeOptions}
                />
            ) : undefined}
        >
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead data-testid="col-status">{i18n.common.status}</TableHead>
                        <TableHead data-testid="col-rule">{i18n.restrictionRules.rule}</TableHead>
                        <TableHead data-testid="col-detection-started-at">{i18n.restrictionRules.detectionStartedAt}</TableHead>
                        <TableHead className="text-right" data-testid="col-identities-evaluated">{i18n.restrictionRules.identitiesEvaluated}</TableHead>
                        <TableHead className="text-right" data-testid="col-violations-found">{i18n.restrictionRules.detectionViolationsFound}</TableHead>
                        <TableHead className="text-right" data-testid="col-violations-resolved">{i18n.restrictionRules.detectionViolationsResolved}</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {detections?.content.map((entry: RestrictionRuleDetectionLog) => (
                        <TableRow key={entry.id}>
                            <TableCell>
                                <InlineRow gap="md">
                                    {getStatusIcon(entry.status)}
                                    <Badge variant={getStatusBadgeVariant(entry.status)}>
                                        {entry.status}
                                    </Badge>
                                </InlineRow>
                            </TableCell>
                            <TableCell>
                                <TextLink
                                    testId={`link-rule-detail-${entry.ruleId}`}
                                    to={routes.accessGovernance.restrictionRules.detail(entry.ruleId)}
                                    variant="bold"
                                >
                                    {entry.ruleName}
                                </TextLink>
                            </TableCell>
                            <TableCell>
                                <IconText icon={Clock} size="sm" className="text-muted-foreground">
                                    {formatTimestamp(entry.detectionStartedAt)}
                                </IconText>
                            </TableCell>
                            <TableCell className="text-right">
                                {entry.identitiesEvaluated}
                            </TableCell>
                            <TableCell className="text-right">
                                {entry.violationsFound}
                            </TableCell>
                            <TableCell className="text-right">
                                {entry.violationsResolved}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </PageLayout>
    )
}
